import React from 'react';

import Overview from './pages/Overview';
import SalesSummary from './pages/SalesSummary';
import TrendsPage from './pages/TrendsPage';
import LocationsPage from './pages/LocationsPage';
import LaborPage from './pages/LaborPage';
import DiscountsPage from './pages/DiscountsPage';
import ProductMixPage from './pages/ProductMixPage';
import SameStoreSalesPage from './pages/SameStoreSalesPage';
import StaffingPage from './pages/StaffingPage';
import RetentionPage from './pages/RetentionPage';
import IntelligencePage from './pages/IntelligencePage';
import GroupComparisonPage from './pages/GroupComparisonPage';
import DigestPage from './pages/DigestPage';
import BenchmarkingPage from './pages/BenchmarkingPage';
import SubscribersPage from './pages/SubscribersPage';
import NewCustomersPage from './pages/NewCustomersPage';
import AdminPage from './pages/AdminPage';
import DoughPlannerPage from './pages/DoughPlannerPage';
import SalesJournalPage from './pages/SalesJournalPage';
import TimesheetsPage from './pages/TimesheetsPage';

// The sidebar. Reports are grouped by the QUESTION they answer, so a reader can
// skip whole sections that aren't their job: a store owner mostly lives in
// Today, Menu & Kitchen and Staff; head office in Sales and Stores.
//
// House rules (nav.test.jsx enforces them):
//   - at most 5 reports per section — past that a heading stops helping;
//   - every report has a help entry in help/content.js under the same id;
//   - the FIRST item overall is the landing/fallback page.
// A section whose items are all hidden for the viewer disappears by itself
// (App.jsx filters empty sections), so tier- or tenant-gated reports never
// leave an empty heading behind.
//
// Item flags: noFilters (no filter bar at all) · storesOnly (store picker only;
// forward-looking pages) · basic (the ONLY items a basic-tier tenant sees —
// must agree with BASIC_OK in backend/src/middleware/reportTierGate.js) ·
// intel (intelligence tier or super admin) · dough (tenant has a `dough`
// block) · adminOnly · accounting (tenant hasn't switched the Accounting
// section off) · bookkeeper (the ONLY items a bookkeeper sees) · roleLabels
// (per-role label overrides).
export const NAV = [
  {
    section: 'Today',
    items: [
      { id: 'digest', label: 'Daily Digest', icon: '📬', page: <DigestPage />, noFilters: true, basic: true },
      { id: 'overview', label: 'Overview', icon: '◧', page: <Overview />, basic: true },
    ],
  },
  {
    section: 'Sales',
    items: [
      { id: 'summary', label: 'Sales Summary', icon: '🧾', page: <SalesSummary />, basic: true },
      { id: 'sss', label: 'Same-Store Sales', icon: '🔁', page: <SameStoreSalesPage /> },
      { id: 'trends', label: 'Trends & Busy Times', icon: '📈', page: <TrendsPage />, basic: true },
      { id: 'discounts', label: 'Discounts & Refunds', icon: '🏷', page: <DiscountsPage />, basic: true },
    ],
  },
  {
    section: 'Menu & Kitchen',
    items: [
      { id: 'productmix', label: 'Product Mix', icon: '🍕', page: <ProductMixPage />, basic: true },
      // Only for tenants with a `dough` block in tenants.json (docs/dough-planner.md).
      { id: 'dough', label: 'Dough Planner', icon: '🫓', page: <DoughPlannerPage />, dough: true, storesOnly: true },
    ],
  },
  {
    section: 'Stores',
    items: [
      { id: 'locations', label: 'Locations', icon: '🏪', page: <LocationsPage />, basic: true },
      { id: 'groupcompare', label: 'Group Comparison', icon: '🆚', page: <GroupComparisonPage /> },
      { id: 'benchmarking', label: 'Benchmarking', icon: '🏅', page: <BenchmarkingPage /> },
    ],
  },
  {
    section: 'Staff',
    items: [
      { id: 'staffing', label: 'Staffing vs Demand', icon: '🧑‍🍳', page: <StaffingPage /> },
      { id: 'labor', label: 'Labor & Productivity', icon: '⏱', page: <LaborPage /> },
    ],
  },
  {
    section: 'Customers',
    items: [
      { id: 'retention', label: 'Customer Retention', icon: '🔂', page: <RetentionPage /> },
      { id: 'newcustomers', label: 'New Customers', icon: '👋', page: <NewCustomersPage /> },
      { id: 'subscribers', label: 'Email Subscribers', icon: '📧', page: <SubscribersPage />, noFilters: true },
      // Hidden unless the tenant is on the intelligence tier or the viewer is
      // a platform (super) admin — base-tier franchise users never see it.
      { id: 'intelligence', label: 'SD Intelligence', icon: '🧠', page: <IntelligencePage />, intel: true },
    ],
  },
  {
    // What the accountant / bookkeeper needs (docs/accounting.md). These are
    // the ONLY reports a bookkeeper sees; the pages own their period picker.
    section: 'Accounting',
    items: [
      { id: 'salesjournal', label: 'Sales Journal', icon: '📒', page: <SalesJournalPage />, accounting: true, bookkeeper: true, storesOnly: true },
      { id: 'timesheets', label: 'Timesheets', icon: '🕒', page: <TimesheetsPage />, accounting: true, bookkeeper: true, storesOnly: true },
    ],
  },
  {
    section: 'Administration',
    items: [
      // Owners (groups + profiles) and bookkeepers (profiles only) get this
      // page too — the label follows the role.
      { id: 'admin', label: 'Users & Access', roleLabels: { owner: 'Store Groups', bookkeeper: 'Accounting Profiles' }, icon: '🔐', page: <AdminPage />, noFilters: true, bookkeeper: true },
    ],
  },
];

export const ALL_ITEMS = NAV.flatMap(s => s.items);
